import React, { useState, useRef, useEffect, useMemo } from "react";
import { 
  Home, Box, Eye, Layers, Compass, Camera, Maximize2, 
  RotateCcw, Sliders, CheckCircle2, AlertTriangle, Plus, 
  Trash2, Download, Save, RefreshCw, Ruler, Hammer, 
  Building2, ShoppingCart, Zap, Cpu, Sparkles, Navigation,
  Move, ChevronRight, Check, Upload, HelpCircle, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getBaseUrl } from "@/lib/apiClient";

const API_BASE_URL = getBaseUrl();

export interface HouseRoom {
  id: string;
  name: string;
  floor: string;
  x: number;
  y: number;
  width_ft: number;
  length_ft: number;
  height_ft: number;
  wall_type: "bloque_6" | "bloque_8" | "drywall" | "columnas_concreto" | "pretil_bloque_concreto";
  color: string;
  floor_material: string;
  furniture: string[];
  doors: Array<{ wall: "front" | "back" | "left" | "right"; pos_ft: number; width_ft: number }>;
  windows: Array<{ wall: "front" | "back" | "left" | "right"; pos_ft: number; width_ft: number }>;
  devices: string[];
}

export const PrometheusSpatialHouseStudio: React.FC = () => {
  // ── Mode States ──
  const [viewMode, setViewMode] = useState<"dollhouse" | "blueprint_2d" | "first_person" | "camera_scan" | "materials_budget" | "iot_digital_twin">("dollhouse");
  const [exportMenuOpen, setExportMenuOpen] = useState<boolean>(false);
  
  // ── IoT Digital Twin State ──
  const [iotDevices, setIotDevices] = useState([
    { id: "dev_cisterna", name: "Bomba Cisterna 0.75 HP", roomName: "Cuarto Técnico / Patio", type: "pump", state: true, metric: "46.5 PSI · Nivel 82% (490 Gal)", gpio: 18 },
    { id: "dev_luz_sala", name: "Circuito Luces LED Sala", roomName: "Sala Principal", type: "light", state: true, metric: "Relé 1 · 45W Activo", gpio: 19 },
    { id: "dev_luz_marquesina", name: "Focos Exteriores Taller", roomName: "Marquesina / Taller", type: "light", state: false, metric: "Relé 2 · Fotocelda", gpio: 21 },
    { id: "dev_solar_bms", name: "Inversor Híbrido & LiFePO4 48V", roomName: "Cuarto Técnico / Patio", type: "solar", state: true, metric: "53.8V · SOC 94% · 680W", gpio: 22 },
    { id: "dev_luma_sensor", name: "Detector de Red LUMA Energy", roomName: "Entrada Principal", type: "grid", state: true, metric: "121.4V 60Hz · Red Estable", gpio: 23 },
  ]);

  const toggleIoTDevice = (devId: string) => {
    setIotDevices(prev => prev.map(d => {
      if (d.id === devId) {
        const nextState = !d.state;
        toast.info(`${d.name}: ${nextState ? "⚡ ENCENDIDO (GPIO " + d.gpio + ")" : "⭕ APAGADO"}`);
        return { ...d, state: nextState };
      }
      return d;
    }));
  };

  // ── CAD/BIM Exporters ──
  const downloadTextFile = (contentStr: string, filename: string, mime: string) => {
    const blob = new Blob([contentStr], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportDXF = () => {
    let dxf = "0\nSECTION\n2\nHEADER\n0\nENDSEC\n0\nSECTION\n2\nTABLES\n0\nTABLE\n2\nLAYER\n";
    dxf += "0\nLAYER\n2\nA-WALL\n70\n0\n62\n7\n6\nCONTINUOUS\n0\n";
    dxf += "0\nLAYER\n2\nA-DOOR\n70\n0\n62\n1\n6\nCONTINUOUS\n0\n";
    dxf += "0\nLAYER\n2\nA-GLAZ\n70\n0\n62\n4\n6\nCONTINUOUS\n0\n";
    dxf += "0\nLAYER\n2\nA-ANNO\n70\n0\n62\n2\n6\nCONTINUOUS\n0\nENDTAB\n0\nENDSEC\n";
    dxf += "0\nSECTION\n2\nENTITIES\n";

    rooms.forEach(r => {
      const x1 = r.x * 12;
      const y1 = r.y * 12;
      const x2 = (r.x + r.width_ft) * 12;
      const y2 = (r.y + r.length_ft) * 12;

      const pts = [
        [x1, y1, x2, y1],
        [x2, y1, x2, y2],
        [x2, y2, x1, y2],
        [x1, y2, x1, y1]
      ];
      pts.forEach(([px1, py1, px2, py2]) => {
        dxf += `0\nLINE\n8\nA-WALL\n10\n${px1}\n20\n${py1}\n30\n0.0\n11\n${px2}\n21\n${py2}\n31\n0.0\n`;
      });

      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      dxf += `0\nTEXT\n8\nA-ANNO\n10\n${cx}\n20\n${cy}\n30\n0.0\n40\n8.0\n1\n${r.name} (${r.width_ft}'x${r.length_ft}')\n`;
    });

    dxf += "0\nENDSEC\n0\nEOF\n";
    downloadTextFile(dxf, `${houseName.replace(/\s+/g, '_')}_plano_arquitectonico.dxf`, "application/dxf");
    toast.success("✓ Plano DXF exportado para AutoCAD / LibreCAD con capas oficiales.");
    setExportMenuOpen(false);
  };

  const handleExportOBJ = () => {
    let obj = `# Prometheus Spatial House Studio 3D Wavefront OBJ\n# ${houseName} - ${houseAddress}\n`;
    let vCount = 1;

    rooms.forEach(r => {
      obj += `o ${r.name.replace(/\s+/g, '_')}\n`;
      const x = r.x * 0.3048;
      const z = r.y * 0.3048;
      const w = r.width_ft * 0.3048;
      const l = r.length_ft * 0.3048;
      const h = r.height_ft * 0.3048;

      obj += `v ${x} 0.0 ${z}\n`;
      obj += `v ${x + w} 0.0 ${z}\n`;
      obj += `v ${x + w} 0.0 ${z + l}\n`;
      obj += `v ${x} 0.0 ${z + l}\n`;

      obj += `v ${x} ${h} ${z}\n`;
      obj += `v ${x + w} ${h} ${z}\n`;
      obj += `v ${x + w} ${h} ${z + l}\n`;
      obj += `v ${x} ${h} ${z + l}\n`;

      const base = vCount;
      obj += `f ${base} ${base + 1} ${base + 2} ${base + 3}\n`;
      obj += `f ${base + 4} ${base + 5} ${base + 6} ${base + 7}\n`;
      obj += `f ${base} ${base + 1} ${base + 5} ${base + 4}\n`;
      obj += `f ${base + 1} ${base + 2} ${base + 6} ${base + 5}\n`;
      obj += `f ${base + 2} ${base + 3} ${base + 7} ${base + 6}\n`;
      obj += `f ${base + 3} ${base} ${base + 4} ${base + 7}\n`;

      vCount += 8;
    });

    downloadTextFile(obj, `${houseName.replace(/\s+/g, '_')}_modelo_3d.obj`, "text/plain");
    toast.success("✓ Malla 3D OBJ exportada para Blender / Three.js con estancias completas.");
    setExportMenuOpen(false);
  };

  const handleExportSTEP = () => {
    let py = `# FreeCAD Script - Prometheus Spatial House BIM Generator\nimport FreeCAD as App\nimport Part\n\ndoc = App.newDocument("${houseName.replace(/\s+/g, '_')}")\n\n`;
    rooms.forEach(r => {
      py += `# Habitación: ${r.name}\n`;
      py += `box_${r.id} = Part.makeBox(${r.width_ft * 304.8}, ${r.length_ft * 304.8}, ${r.height_ft * 304.8})\n`;
      py += `box_${r.id}.translate(App.Vector(${r.x * 304.8}, ${r.y * 304.8}, 0))\n`;
      py += `Part.show(box_${r.id}, "${r.name.replace(/\s+/g, '_')}")\n\n`;
    });
    py += `doc.recompute()\nprint("Modelo de residencia generado exitosamente.")\n`;

    downloadTextFile(py, `${houseName.replace(/\s+/g, '_')}_freecad_bim.py`, "text/x-python");
    toast.success("✓ Script BIM FreeCAD exportado para modelado paramétrico sólido.");
    setExportMenuOpen(false);
  };
  const [activeLevel, setActiveLevel] = useState<string>("Planta 1");
  const [selectedRoomId, setSelectedRoomId] = useState<string>("room_sala");
  const [mobileTab, setMobileTab] = useState<"viewport" | "rooms">("viewport");

  // ── House Data ──
  const [houseName, setHouseName] = useState("Residencia Modelo Puerto Rico");
  const [houseAddress, setHouseAddress] = useState("Bayamón, Puerto Rico");
  const [rooms, setRooms] = useState<HouseRoom[]>([]);
  const [isLoadingHouse, setIsLoadingHouse] = useState(true);

  // ── 3D View Transforms ──
  const [yaw, setYaw] = useState<number>(45); // horizontal rotation (deg)
  const [pitch, setPitch] = useState<number>(35); // vertical elevation (deg)
  const [zoom, setZoom] = useState<number>(1.0);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [cutawayWalls, setCutawayWalls] = useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // ── First Person Walkthrough ──
  const [fpYaw, setFpYaw] = useState<number>(0);
  const [fpPos, setFpPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // ── Camera Scanner State ──
  const [scanStep, setScanStep] = useState<number>(1);
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [cornersCaptured, setCornersCaptured] = useState<Array<{ x: number; y: number }>>([]);
  const [newRoomType, setNewRoomType] = useState<string>("bedroom");
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ── Renovation / Add Room Modal ──
  const [addRoomModalOpen, setAddRoomModalOpen] = useState<boolean>(false);
  const [newRoomName, setNewRoomName] = useState<string>("");
  const [newRoomW, setNewRoomW] = useState<number>(14);
  const [newRoomL, setNewRoomL] = useState<number>(12);
  const [newRoomWall, setNewRoomWall] = useState<"bloque_6" | "bloque_8" | "drywall">("bloque_6");

  // Load house layout from backend
  const loadHouse = async () => {
    setIsLoadingHouse(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/prometheus/spatial/house`);
      if (res.ok) {
        const data = await res.json();
        if (data.house) {
          setHouseName(data.house.name || "Residencia Puerto Rico");
          setHouseAddress(data.house.address || "Bayamón, PR");
          setRooms(data.house.rooms || []);
          if (data.house.rooms && data.house.rooms.length > 0) {
            setSelectedRoomId(data.house.rooms[0].id);
          }
        }
      }
    } catch (err) {
      console.warn("Using local fallback house model:", err);
    } finally {
      setIsLoadingHouse(false);
    }
  };

  useEffect(() => {
    loadHouse();
  }, []);

  // Filter rooms by selected floor/level
  const currentFloorRooms = useMemo(() => {
    return rooms.filter(r => r.floor === activeLevel);
  }, [rooms, activeLevel]);

  const selectedRoom = useMemo(() => {
    return rooms.find(r => r.id === selectedRoomId) || rooms[0] || null;
  }, [rooms, selectedRoomId]);

  // Total House Calculations
  const totalSqft = useMemo(() => {
    return rooms
      .filter(r => r.floor === "Planta 1")
      .reduce((acc, r) => acc + (r.width_ft * r.length_ft), 0);
  }, [rooms]);

  // Total Materials Takeoff (Puerto Rico Construction Norms)
  const materialsTakeoff = useMemo(() => {
    let totalPerimeterLinearFt = 0;
    let totalFloorSqft = 0;
    let totalWallAreaSqft = 0;

    currentFloorRooms.forEach(r => {
      const perimeter = (r.width_ft + r.length_ft) * 2;
      totalPerimeterLinearFt += perimeter;
      totalFloorSqft += (r.width_ft * r.length_ft);
      totalWallAreaSqft += perimeter * r.height_ft;
    });

    // 1 bloque de hormigón (8"x8"x16") = 0.89 sqft de pared. Con desperdicio: factor 1.125
    const concreteBlocks6 = Math.round(totalWallAreaSqft * 0.7 * 1.125); // 70% paredes interiores/exteriores
    const concreteBlocks8 = Math.round(totalWallAreaSqft * 0.3 * 1.125); // 30% paredes de carga perimetrales
    const cementBags = Math.round((concreteBlocks6 + concreteBlocks8) * 0.045 + (totalFloorSqft * 0.08));
    const rebarSticksHalfInch = Math.round(totalPerimeterLinearFt * 0.6); // varilla #4 a 24" O.C.
    const sandM3 = Math.round(cementBags * 0.12 * 10) / 10;
    const gravelM3 = Math.round(cementBags * 0.16 * 10) / 10;
    const drywallSheets = Math.round((totalWallAreaSqft * 0.25) / 32); // 4x8 sheet
    const woodStudsPT = Math.round(drywallSheets * 3.5);

    // Cost estimates based on Rexville Store #6405
    const costBlocks = (concreteBlocks6 * 1.85) + (concreteBlocks8 * 2.25);
    const costCement = cementBags * 8.50;
    const costRebar = rebarSticksHalfInch * 9.75;
    const costAggregate = (sandM3 * 38) + (gravelM3 * 42);
    const costDrywall = drywallSheets * 16.50;
    const costWood = woodStudsPT * 7.25;
    const grandTotalUSD = costBlocks + costCement + costRebar + costAggregate + costDrywall + costWood;

    return {
      totalWallAreaSqft,
      totalFloorSqft,
      concreteBlocks6,
      concreteBlocks8,
      cementBags,
      rebarSticksHalfInch,
      sandM3,
      gravelM3,
      drywallSheets,
      woodStudsPT,
      grandTotalUSD
    };
  }, [currentFloorRooms]);

  // ── 3D DOLLHOUSE CANVAS RENDERING ENGINE ──
  useEffect(() => {
    if (viewMode !== "dollhouse") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2 + panOffset.x;
      const cy = h / 2 + panOffset.y + 40;

      // Projection angles
      const radYaw = (yaw * Math.PI) / 180;
      const radPitch = (pitch * Math.PI) / 180;
      const scale = 14 * zoom;

      // Project 3D (X, Y, Z) into 2D Screen
      const project = (x: number, y: number, z: number) => {
        // Rotate around Y axis (yaw)
        const cosY = Math.cos(radYaw);
        const sinY = Math.sin(radYaw);
        const rx = x * cosY - z * sinY;
        const rz = x * sinY + z * cosY;

        // Rotate around X axis (pitch)
        const cosP = Math.cos(radPitch);
        const sinP = Math.sin(radPitch);
        const ry = y * cosP - rz * sinP;
        const depth = y * sinP + rz * cosP;

        return {
          sx: cx + rx * scale,
          sy: cy - ry * scale,
          depth
        };
      };

      // Draw Ground Grid Pattern
      ctx.strokeStyle = "rgba(148, 163, 184, 0.12)";
      ctx.lineWidth = 1;
      const gridSize = 40;
      for (let gx = -gridSize; gx <= gridSize; gx += 5) {
        const p1 = project(gx, 0, -gridSize);
        const p2 = project(gx, 0, gridSize);
        ctx.beginPath();
        ctx.moveTo(p1.sx, p1.sy);
        ctx.lineTo(p2.sx, p2.sy);
        ctx.stroke();
      }
      for (let gz = -gridSize; gz <= gridSize; gz += 5) {
        const p1 = project(-gridSize, 0, gz);
        const p2 = project(gridSize, 0, gz);
        ctx.beginPath();
        ctx.moveTo(p1.sx, p1.sy);
        ctx.lineTo(p2.sx, p2.sy);
        ctx.stroke();
      }

      // Center offset of house model
      const houseCenterX = 16;
      const houseCenterZ = 14;

      // Render Each Room in Dollhouse 3D
      currentFloorRooms.forEach(room => {
        const isSelected = room.id === selectedRoomId;
        const x0 = room.x - houseCenterX;
        const z0 = room.y - houseCenterZ;
        const x1 = x0 + room.width_ft;
        const z1 = z0 + room.length_ft;
        const wallH = cutawayWalls ? Math.min(4.0, room.height_ft * 0.45) : room.height_ft;

        // 4 Floor Corners
        const c0 = project(x0, 0, z0);
        const c1 = project(x1, 0, z0);
        const c2 = project(x1, 0, z1);
        const c3 = project(x0, 0, z1);

        // 1. Draw Floor Slab
        ctx.beginPath();
        ctx.moveTo(c0.sx, c0.sy);
        ctx.lineTo(c1.sx, c1.sy);
        ctx.lineTo(c2.sx, c2.sy);
        ctx.lineTo(c3.sx, c3.sy);
        ctx.closePath();

        if (isSelected) {
          ctx.fillStyle = "rgba(6, 182, 212, 0.35)";
          ctx.strokeStyle = "#06b6d4";
          ctx.lineWidth = 2.5;
        } else {
          ctx.fillStyle = room.floor_material.includes("ceramica") ? "rgba(241, 245, 249, 0.18)" :
                          room.floor_material.includes("madera") ? "rgba(180, 83, 9, 0.20)" :
                          room.floor_material.includes("concreto") ? "rgba(100, 116, 139, 0.25)" : "rgba(255, 255, 255, 0.15)";
          ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
          ctx.lineWidth = 1.2;
        }
        ctx.fill();
        ctx.stroke();

        // 2. Extrude Walls (Cutaway Dollhouse Style)
        const t0 = project(x0, wallH, z0);
        const t1 = project(x1, wallH, z0);
        const t2 = project(x1, wallH, z1);
        const t3 = project(x0, wallH, z1);

        const drawWallFace = (pA: any, pB: any, pTA: any, pTB: any, shadeAlpha: number) => {
          ctx.beginPath();
          ctx.moveTo(pA.sx, pA.sy);
          ctx.lineTo(pB.sx, pB.sy);
          ctx.lineTo(pTB.sx, pTB.sy);
          ctx.lineTo(pTA.sx, pTA.sy);
          ctx.closePath();
          ctx.fillStyle = isSelected ? `rgba(6, 182, 212, ${shadeAlpha * 1.4})` : `rgba(226, 232, 240, ${shadeAlpha})`;
          ctx.strokeStyle = isSelected ? "#22d3ee" : "rgba(255, 255, 255, 0.5)";
          ctx.lineWidth = 1;
          ctx.fill();
          ctx.stroke();
        };

        // Draw Walls depending on viewing angle
        drawWallFace(c0, c1, t0, t1, 0.25); // Back wall
        drawWallFace(c1, c2, t1, t2, 0.35); // Right wall
        drawWallFace(c2, c3, t2, t3, 0.15); // Front wall
        drawWallFace(c3, c0, t3, t0, 0.30); // Left wall

        // 3. Render Furniture Icons/Blocks in 3D
        if (room.furniture && room.furniture.length > 0) {
          const fMid = project(x0 + room.width_ft * 0.5, 0.5, z0 + room.length_ft * 0.5);
          ctx.fillStyle = isSelected ? "#38bdf8" : "#94a3b8";
          ctx.font = "bold 9px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`🛋️ ${room.furniture[0]}`, fMid.sx, fMid.sy);
        }

        // 4. Room Label in Center of Floor
        const midP = project(x0 + room.width_ft * 0.5, 0, z0 + room.length_ft * 0.5);
        ctx.fillStyle = isSelected ? "#22d3ee" : "#f8fafc";
        ctx.font = isSelected ? "bold 11px monospace" : "10px monospace";
        ctx.textAlign = "center";
        ctx.fillText(room.name, midP.sx, midP.sy - 8);

        ctx.font = "9px monospace";
        ctx.fillStyle = "rgba(148, 163, 184, 0.9)";
        ctx.fillText(`${room.width_ft}' × ${room.length_ft}' (${Math.round(room.width_ft * room.length_ft)} ft²)`, midP.sx, midP.sy + 6);

        // IoT Device Badge
        if (room.devices && room.devices.length > 0) {
          ctx.fillStyle = "#10b981";
          ctx.font = "bold 9px monospace";
          ctx.fillText(`⚡ ${room.devices.length} IoT Online`, midP.sx, midP.sy + 18);
        }
      });
    };

    render();
  }, [viewMode, yaw, pitch, zoom, panOffset, currentFloorRooms, selectedRoomId, cutawayWalls]);

  // ── MOUSE CONTROLS FOR 3D CANVAS ──
  const handleMouseDownCanvas = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMoveCanvas = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    if (e.shiftKey || e.button === 1) {
      // Pan
      setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    } else {
      // Rotate
      setYaw(prev => (prev + dx * 0.6) % 360);
      setPitch(prev => Math.max(10, Math.min(85, prev - dy * 0.4)));
    }
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUpCanvas = () => {
    setIsDragging(false);
  };

  // ── CAMERA SCANNER HANDLERS ──
  const startCameraScan = async () => {
    setCameraActive(true);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      toast.success("📷 Cámara de escaneo espacial activada.");
    } catch (err) {
      console.warn("No camera access:", err);
      toast.error("No se pudo acceder a la cámara.");
      setCameraActive(false);
    }
  };

  const stopCameraScan = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const handleCaptureCorner = () => {
    if (scanStep < 4) {
      setScanStep(s => s + 1);
      toast.info(`✓ Esquina ${scanStep} registrada. Ahora enfoca la siguiente.`);
    } else if (scanStep === 4) {
      setScanStep(5);
      toast.info("✓ 4 esquinas capturadas. Ahora enfoca la unión de pared con el techo.");
    } else {
      // Finalize Room
      const newRoom: HouseRoom = {
        id: `room_${Date.now()}`,
        name: `Habitación Escaneada (${newRoomType})`,
        floor: activeLevel,
        x: 28,
        y: 0,
        width_ft: 14.0,
        length_ft: 12.0,
        height_ft: 9.0,
        wall_type: "bloque_6",
        color: "#e2e8f0",
        floor_material: "ceramica_clara",
        furniture: ["Mesa de Trabajo", "Estantería"],
        doors: [{ wall: "front", pos_ft: 4.0, width_ft: 2.8 }],
        windows: [{ wall: "back", pos_ft: 6.0, width_ft: 3.5 }],
        devices: []
      };
      setRooms(prev => [...prev, newRoom]);
      setSelectedRoomId(newRoom.id);
      stopCameraScan();
      setViewMode("dollhouse");
      toast.success("🎉 ¡Habitación digitalizada en 3D e incorporada a tu casa!");
      setScanStep(1);
    }
  };

  // Add Room Manually
  const handleAddRoomConfirm = () => {
    if (!newRoomName.trim()) {
      toast.error("Ingresa un nombre para la habitación o extensión.");
      return;
    }
    const newR: HouseRoom = {
      id: `room_${Date.now()}`,
      name: newRoomName,
      floor: activeLevel,
      x: 14 + Math.random() * 8,
      y: 14 + Math.random() * 8,
      width_ft: newRoomW,
      length_ft: newRoomL,
      height_ft: 9.0,
      wall_type: newRoomWall,
      color: "#f1f5f9",
      floor_material: "concreto_pulido",
      furniture: ["Mesa", "Sillas"],
      doors: [{ wall: "front", pos_ft: 4.0, width_ft: 3.0 }],
      windows: [{ wall: "right", pos_ft: 5.0, width_ft: 3.5 }],
      devices: []
    };

    setRooms(prev => [...prev, newR]);
    setSelectedRoomId(newR.id);
    setAddRoomModalOpen(false);
    toast.success(`✓ "${newRoomName}" añadida al plano de la casa.`);
  };

  return (
    <div className="w-full flex flex-col h-[85vh] min-h-[700px] bg-background border border-border rounded-2xl overflow-hidden font-mono-tech select-none shadow-2xl">
      {/* Top Header Bar */}
      <div className="min-h-14 py-2 md:h-14 bg-card border-b border-border px-3 md:px-4 flex flex-wrap md:flex-nowrap items-center justify-between gap-2 z-20 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
            <Home className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
              Prometheus Spatial House & Room 3D Studio
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 font-extrabold border border-blue-500/30">
                BIM / DIGITAL TWIN PR
              </span>
            </h2>
            <p className="text-[10px] text-muted-foreground">
              {houseName} • {houseAddress} • Superficie: {totalSqft} ft² ({Math.round(totalSqft * 0.0929)} m²)
            </p>
          </div>
        </div>

        {/* Mobile View Toggle (Visor vs Estancias) */}
        <div className="flex md:hidden items-center gap-1 bg-muted/40 p-1 rounded-xl border border-border">
          <button
            type="button"
            onClick={() => setMobileTab("viewport")}
            className={cn(
              "px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer",
              mobileTab === "viewport" ? "bg-blue-600 text-white shadow" : "text-muted-foreground"
            )}
          >
            Visor 3D / Plano
          </button>
          <button
            type="button"
            onClick={() => setMobileTab("rooms")}
            className={cn(
              "px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer",
              mobileTab === "rooms" ? "bg-blue-600 text-white shadow" : "text-muted-foreground"
            )}
          >
            Estancias ({currentFloorRooms.length})
          </button>
        </div>

        {/* Level Switcher & View Mode Toggles */}
        <div className="flex items-center gap-2">
          {/* Level (Planta 1 / Azotea) */}
          <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-xl border border-border">
            {["Planta 1", "Techo / Azotea"].map(lvl => (
              <button
                key={lvl}
                type="button"
                onClick={() => setActiveLevel(lvl)}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer",
                  activeLevel === lvl ? "bg-blue-600 text-white shadow-md" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {lvl}
              </button>
            ))}
          </div>

          {/* View Modes */}
          <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-xl border border-border overflow-x-auto max-w-full">
            <button
              type="button"
              onClick={() => { setViewMode("dollhouse"); stopCameraScan(); }}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 cursor-pointer transition-all",
                viewMode === "dollhouse" ? "bg-blue-600 text-white shadow" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Box className="w-3.5 h-3.5" /> 3D Dollhouse
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("blueprint_2d"); stopCameraScan(); }}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 cursor-pointer transition-all",
                viewMode === "blueprint_2d" ? "bg-blue-600 text-white shadow" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Ruler className="w-3.5 h-3.5" /> Plano 2D
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("first_person"); stopCameraScan(); }}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 cursor-pointer transition-all",
                viewMode === "first_person" ? "bg-blue-600 text-white shadow" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Navigation className="w-3.5 h-3.5" /> 1ª Persona
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("camera_scan"); startCameraScan(); }}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 cursor-pointer transition-all",
                viewMode === "camera_scan" ? "bg-amber-600 text-white shadow" : "text-amber-400 hover:text-amber-300"
              )}
            >
              <Camera className="w-3.5 h-3.5" /> Escáner AR
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("materials_budget"); stopCameraScan(); }}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 cursor-pointer transition-all",
                viewMode === "materials_budget" ? "bg-emerald-600 text-white shadow" : "text-emerald-400 hover:text-emerald-300"
              )}
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Materiales PR
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("iot_digital_twin"); stopCameraScan(); }}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-lg flex items-center gap-1 cursor-pointer transition-all",
                viewMode === "iot_digital_twin" ? "bg-cyan-600 text-white shadow" : "text-cyan-400 hover:text-cyan-300"
              )}
            >
              <Zap className="w-3.5 h-3.5" /> Gemelo IoT
            </button>
          </div>

          {/* Export CAD/BIM Dropdown */}
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-600/30 flex items-center gap-1 cursor-pointer transition-all shadow-xs"
            >
              <Download className="w-3.5 h-3.5" /> Exportar CAD/BIM
            </button>

            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1.5 z-50 w-52 p-1.5 rounded-xl bg-card border-2 border-border shadow-2xl space-y-0.5 text-xs">
                <button
                  type="button"
                  onClick={handleExportDXF}
                  className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-muted text-foreground flex items-center gap-2 cursor-pointer"
                >
                  <Ruler className="w-3.5 h-3.5 text-blue-400" />
                  <div>
                    <span className="font-bold block text-[11px]">Plano DXF (AutoCAD)</span>
                    <span className="text-[9px] text-muted-foreground">Capas A-WALL, A-DOOR, cotas</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleExportOBJ}
                  className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-muted text-foreground flex items-center gap-2 cursor-pointer"
                >
                  <Box className="w-3.5 h-3.5 text-amber-400" />
                  <div>
                    <span className="font-bold block text-[11px]">Malla 3D OBJ (Blender)</span>
                    <span className="text-[9px] text-muted-foreground">Geometría tridimensional</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleExportSTEP}
                  className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-muted text-foreground flex items-center gap-2 cursor-pointer"
                >
                  <Building2 className="w-3.5 h-3.5 text-emerald-400" />
                  <div>
                    <span className="font-bold block text-[11px]">Script BIM (FreeCAD)</span>
                    <span className="text-[9px] text-muted-foreground">Sólidos paramétricos</span>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setAddRoomModalOpen(true)}
            className="px-3 py-1.5 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/40 text-xs font-bold flex items-center gap-1 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Nueva Extensión
          </button>
        </div>
      </div>

      {/* Main Studio Body */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Left Sidebar: Room List & Properties */}
        <div className={cn("w-full md:w-72 bg-card/70 border-r border-border flex flex-col shrink-0 z-10 text-xs", mobileTab === "viewport" ? "hidden md:flex" : "flex")}>
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="font-bold uppercase tracking-wider text-muted-foreground text-[10px]">
              Estancias ({currentFloorRooms.length})
            </span>
            <span className="text-[10px] text-blue-400 font-bold">{activeLevel}</span>
          </div>

          {/* Rooms List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 custom-scrollbar">
            {currentFloorRooms.map(room => (
              <div
                key={room.id}
                onClick={() => setSelectedRoomId(room.id)}
                className={cn(
                  "p-2.5 rounded-xl border transition-all cursor-pointer",
                  selectedRoomId === room.id
                    ? "border-blue-500/80 bg-blue-500/15 shadow-[0_0_12px_rgba(59,130,246,0.2)]"
                    : "border-border/80 bg-background/50 hover:border-border"
                )}
              >
                <div className="flex items-center justify-between font-bold mb-1">
                  <span className="text-foreground truncate">{room.name}</span>
                  <span className="text-[9px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground">
                    {Math.round(room.width_ft * room.length_ft)} ft²
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center justify-between">
                  <span>{room.width_ft}' × {room.length_ft}' (H: {room.height_ft}')</span>
                  <span className="text-blue-300 font-mono text-[9px] uppercase">{room.wall_type.replace("_", " ")}</span>
                </div>
                {room.devices && room.devices.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-1 text-[9px] text-emerald-400">
                    <Zap className="w-3 h-3" />
                    <span>{room.devices.length} dispositivo(s) IoT enlazados</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Selected Room Inspector Panel */}
          {selectedRoom && (
            <div className="p-3 border-t border-border bg-muted/20 space-y-2">
              <div className="text-[10px] font-bold uppercase text-muted-foreground flex items-center justify-between">
                <span>Inspector: {selectedRoom.name}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div className="bg-background/80 p-1.5 rounded border border-border">
                  <span className="text-muted-foreground block text-[9px]">Ancho (X):</span>
                  <span className="font-bold">{selectedRoom.width_ft} ft ({Math.round(selectedRoom.width_ft * 0.3048 * 10) / 10} m)</span>
                </div>
                <div className="bg-background/80 p-1.5 rounded border border-border">
                  <span className="text-muted-foreground block text-[9px]">Largo (Y):</span>
                  <span className="font-bold">{selectedRoom.length_ft} ft ({Math.round(selectedRoom.length_ft * 0.3048 * 10) / 10} m)</span>
                </div>
              </div>
              <div className="bg-background/80 p-1.5 rounded border border-border text-[10px]">
                <span className="text-muted-foreground block text-[9px]">Tipo de Pared Estructural:</span>
                <span className="font-bold text-blue-300">{selectedRoom.wall_type.toUpperCase()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Center Canvas / Viewports */}
        <div className={cn("flex-1 relative overflow-hidden bg-[#070b10] flex flex-col", mobileTab === "rooms" ? "hidden md:flex" : "flex")}>
          {/* 1. DOLLHOUSE 3D VIEW */}
          {viewMode === "dollhouse" && (
            <div className="w-full h-full relative cursor-grab active:cursor-grabbing">
              <canvas
                ref={canvasRef}
                width={1200}
                height={750}
                className="w-full h-full block"
                onMouseDown={handleMouseDownCanvas}
                onMouseMove={handleMouseMoveCanvas}
                onMouseUp={handleMouseUpCanvas}
              />

              {/* Floating 3D Controls */}
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-card/80 p-1.5 rounded-xl border border-border backdrop-blur-md text-[10px] z-10">
                <button
                  type="button"
                  onClick={() => setCutawayWalls(w => !w)}
                  className={cn(
                    "px-2.5 py-1 rounded-lg font-bold border transition-colors cursor-pointer",
                    cutawayWalls ? "bg-blue-600 text-white border-blue-400" : "bg-muted text-muted-foreground"
                  )}
                >
                  {cutawayWalls ? "Paredes Seccionadas (Corte)" : "Paredes Completas"}
                </button>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">Arrastra para rotar • Shift + Arrastra para desplazar</span>
              </div>

              <div className="absolute bottom-4 right-4 flex items-center gap-1 bg-card/80 p-1.5 rounded-xl border border-border backdrop-blur-md z-10">
                <button
                  type="button"
                  onClick={() => setZoom(z => Math.min(2.0, z + 0.15))}
                  className="w-7 h-7 rounded-lg hover:bg-muted text-foreground flex items-center justify-center font-bold"
                  title="Acercar"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(z => Math.max(0.4, z - 0.15))}
                  className="w-7 h-7 rounded-lg hover:bg-muted text-foreground flex items-center justify-center font-bold"
                  title="Alejar"
                >
                  -
                </button>
                <button
                  type="button"
                  onClick={() => { setYaw(45); setPitch(35); setZoom(1.0); setPanOffset({ x: 0, y: 0 }); }}
                  className="w-7 h-7 rounded-lg hover:bg-muted text-foreground flex items-center justify-center"
                  title="Restablecer Isométrica"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* 2. BLUEPRINT 2D CAD FLOOR PLAN */}
          {viewMode === "blueprint_2d" && (
            <div className="w-full h-full relative overflow-auto p-8 bg-dot-pattern flex items-center justify-center">
              <div className="relative bg-[#0d1520] border-2 border-blue-500/50 rounded-2xl p-6 shadow-2xl min-w-[700px] min-h-[500px]">
                <div className="text-[10px] font-bold uppercase text-blue-400 mb-4 pb-2 border-b border-blue-500/30 flex items-center justify-between">
                  <span>Plano Arquitectónico Acotado • {activeLevel}</span>
                  <span>Escala 1:50 • Cotas en Pies y Metros</span>
                </div>

                {/* Rooms Grid Blueprint */}
                <div className="relative w-[650px] h-[440px] border border-dashed border-blue-500/30 rounded-xl bg-blue-950/10">
                  {currentFloorRooms.map(room => {
                    const isSelected = room.id === selectedRoomId;
                    const scaleX = 14;
                    const scaleY = 11;

                    return (
                      <div
                        key={room.id}
                        onClick={() => setSelectedRoomId(room.id)}
                        style={{
                          left: `${room.x * scaleX}px`,
                          top: `${room.y * scaleY}px`,
                          width: `${room.width_ft * scaleX}px`,
                          height: `${room.length_ft * scaleY}px`
                        }}
                        className={cn(
                          "absolute border-2 transition-all p-2 flex flex-col justify-between cursor-pointer rounded",
                          isSelected
                            ? "border-blue-400 bg-blue-500/25 shadow-lg shadow-blue-500/30"
                            : "border-slate-400/60 bg-slate-900/50 hover:border-slate-300"
                        )}
                      >
                        {/* Top Cota Dimension */}
                        <div className="text-[8px] font-bold text-center text-blue-300 border-b border-blue-500/20 pb-0.5">
                          ↔ {room.width_ft}' ({Math.round(room.width_ft * 0.3048 * 10) / 10}m)
                        </div>

                        {/* Room Center Info */}
                        <div className="text-center my-auto">
                          <span className="font-bold text-xs block text-foreground">{room.name}</span>
                          <span className="text-[9px] text-muted-foreground block">
                            {Math.round(room.width_ft * room.length_ft)} ft² • {room.wall_type.toUpperCase()}
                          </span>
                        </div>

                        {/* Side Cota Dimension */}
                        <div className="text-[8px] font-bold text-right text-muted-foreground">
                          ↕ {room.length_ft}' ({Math.round(room.length_ft * 0.3048 * 10) / 10}m)
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 3. FIRST PERSON INTERIOR WALKTHROUGH */}
          {viewMode === "first_person" && (
            <div className="w-full h-full relative bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
              <div className="max-w-md p-6 rounded-2xl bg-card border border-border shadow-2xl space-y-4">
                <Navigation className="w-10 h-10 text-blue-400 mx-auto animate-pulse" />
                <h3 className="text-sm font-bold text-foreground">
                  Recorrido Virtual en Primera Persona: {selectedRoom?.name}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Estás situado a 5.5 pies (1.67 m) sobre el piso en el centro de {selectedRoom?.name}.
                  Dimensiones de campo visual: {selectedRoom?.width_ft}' × {selectedRoom?.length_ft}' con altura de losa a {selectedRoom?.height_ft}' ft.
                </p>
                <div className="p-3 bg-muted/40 rounded-xl text-xs space-y-1 font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pared Principal:</span>
                    <span className="font-bold">{selectedRoom?.wall_type.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Piso Instalado:</span>
                    <span className="font-bold">{selectedRoom?.floor_material}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Mobiliario / Taller:</span>
                    <span className="font-bold text-blue-300">{selectedRoom?.furniture.join(", ") || "Espacio Abierto"}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 4. CAMERA AR SCANNER */}
          {viewMode === "camera_scan" && (
            <div className="w-full h-full relative bg-black flex flex-col items-center justify-center">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />

              {/* HUD Reticle & Instructions */}
              <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-between p-6">
                <div className="bg-black/70 backdrop-blur-md px-4 py-2 rounded-xl border border-amber-500/40 text-center">
                  <span className="text-xs font-bold text-amber-300 block">
                    Escáner Espacial AR: Paso {scanStep} de 5
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {scanStep <= 4 ? `Apunta y alinea el retículo con la Esquina #${scanStep} del cuarto` : "Enfoca la cota de altura (unión pared y techo)"}
                  </span>
                </div>

                {/* Center Reticle */}
                <div className="w-24 h-24 border-2 border-dashed border-amber-400 rounded-2xl flex items-center justify-center animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                </div>

                {/* Capture Button */}
                <div className="pointer-events-auto">
                  <button
                    type="button"
                    onClick={handleCaptureCorner}
                    className="px-6 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs shadow-xl shadow-amber-500/40 cursor-pointer flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" /> Registrar Punto de Esquina
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 5. PUERTO RICO MATERIALS & HOME DEPOT REXVILLE TAKEOFF */}
          {viewMode === "iot_digital_twin" && (
            <div className="w-full h-full p-4 md:p-6 overflow-y-auto space-y-4 bg-void/50 custom-scrollbar">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-card p-4 rounded-xl border border-cyan-500/30">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400">
                    <Zap className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-cyan-300">Gemelo Digital IoT & Telemetría Crítica</h3>
                    <p className="text-[10px] text-muted-foreground font-sans">
                      Conmutación en tiempo real de relés, monitor de cisterna y banco solar LiFePO4
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold">
                    ESP32 Mesh: Conectado (915 MHz)
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {iotDevices.map(dev => (
                  <div
                    key={dev.id}
                    className={cn(
                      "p-4 rounded-xl border transition-all space-y-3 bg-card",
                      dev.state ? "border-cyan-500/50 shadow-md shadow-cyan-500/10" : "border-border/60 opacity-80"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[9px] font-bold text-muted-foreground uppercase">{dev.roomName}</span>
                        <h4 className="text-xs font-bold text-foreground mt-0.5">{dev.name}</h4>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleIoTDevice(dev.id)}
                        className={cn(
                          "px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition-all",
                          dev.state ? "bg-cyan-500 text-black shadow-md font-black" : "bg-muted text-muted-foreground"
                        )}
                      >
                        {dev.state ? "ACTIVO" : "INACTIVO"}
                      </button>
                    </div>
                    <div className="p-2 rounded-lg bg-void/60 border border-border/40 text-[10px] font-mono text-muted-foreground flex items-center justify-between">
                      <span>{dev.metric}</span>
                      {dev.gpio && <span className="text-cyan-400">GPIO {dev.gpio}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {viewMode === "materials_budget" && (
            <div className="w-full h-full overflow-y-auto p-6 space-y-4 custom-scrollbar text-xs">
              <div className="p-4 rounded-2xl bg-emerald-950/20 border border-emerald-500/30 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-emerald-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Cómputo de Obra Civil & Remodelación (Puerto Rico)
                  </h3>
                  <p className="text-[10px] text-muted-foreground">
                    Calculado según Código de Construcción PR 2018 (ASCE 7 170+ mph) para {activeLevel}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-muted-foreground block">Presupuesto Estimado:</span>
                  <span className="text-lg font-extrabold text-emerald-400">${materialsTakeoff.grandTotalUSD.toFixed(2)} USD</span>
                </div>
              </div>

              {/* Items Breakdown Table */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-3 bg-muted/40 font-bold text-[10px] uppercase text-muted-foreground border-b border-border flex items-center justify-between">
                  <span>Material / Insumo Estructural</span>
                  <span>Ubicación Home Depot Rexville #6405</span>
                </div>
                <div className="divide-y divide-border/60">
                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold block">Bloques de Hormigón 6" y 8" (Paredes)</span>
                      <span className="text-[10px] text-muted-foreground">
                        {materialsTakeoff.concreteBlocks6} bloques de 6" + {materialsTakeoff.concreteBlocks8} bloques de 8"
                      </span>
                    </div>
                    <span className="text-emerald-400 font-bold">Pasillo 1–2, Bahías 001–020</span>
                  </div>

                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold block">Cemento Portland Gris (Ponce / San Juan)</span>
                      <span className="text-[10px] text-muted-foreground">{materialsTakeoff.cementBags} sacos de 47kg</span>
                    </div>
                    <span className="text-emerald-400 font-bold">Pasillo 1, Bahía 005</span>
                  </div>

                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold block">Varilla de Refuerzo de Acero #4 (1/2" × 20ft)</span>
                      <span className="text-[10px] text-muted-foreground">{materialsTakeoff.rebarSticksHalfInch} varillas con amarre cada 24" O.C.</span>
                    </div>
                    <span className="text-emerald-400 font-bold">Pasillo 1–2, Bahía 018</span>
                  </div>

                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold block">Arena de Empañete y Gravilla de Concreto</span>
                      <span className="text-[10px] text-muted-foreground">{materialsTakeoff.sandM3} m³ arena + {materialsTakeoff.gravelM3} m³ gravilla</span>
                    </div>
                    <span className="text-emerald-400 font-bold">Pasillo 2 (Yard Exterior)</span>
                  </div>

                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold block">Madera Estructural Tratada Pressure Treated 2×4 / 2×6</span>
                      <span className="text-[10px] text-muted-foreground">{materialsTakeoff.woodStudsPT} unidades (resistente a polilla y salitre)</span>
                    </div>
                    <span className="text-emerald-400 font-bold">Pasillo 5–6, Bahías 001–024</span>
                  </div>

                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold block">Tornillería Tapcon & Anclajes Simpson Strong-Tie H2.5A</span>
                      <span className="text-[10px] text-muted-foreground">Anclajes para concreto y fijación de vigas contra huracán</span>
                    </div>
                    <span className="text-emerald-400 font-bold">Pasillo 8–9, Bahías 001–020</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal: Add Room / Extension */}
      {addRoomModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md bg-card border border-blue-500/40 rounded-2xl p-5 space-y-4 shadow-2xl font-mono-tech text-xs">
            <h3 className="text-sm font-bold text-blue-300 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Proyectar Nueva Habitación o Extensión
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold text-muted-foreground block mb-1">Nombre:</label>
                <input
                  type="text"
                  value={newRoomName}
                  onChange={e => setNewRoomName(e.target.value)}
                  placeholder="Ej: Terraza Techada, Cuarto 3, Marquesina"
                  className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground font-semibold"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-muted-foreground block mb-1">Ancho (pies):</label>
                  <input
                    type="number"
                    value={newRoomW}
                    onChange={e => setNewRoomW(parseFloat(e.target.value) || 10)}
                    className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground font-semibold"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-muted-foreground block mb-1">Largo (pies):</label>
                  <input
                    type="number"
                    value={newRoomL}
                    onChange={e => setNewRoomL(parseFloat(e.target.value) || 10)}
                    className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground font-semibold"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-muted-foreground block mb-1">Tipo de Construcción:</label>
                <select
                  value={newRoomWall}
                  onChange={e => setNewRoomWall(e.target.value as any)}
                  className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground font-semibold"
                >
                  <option value="bloque_6">Bloque de Cemento 6" (Estándar PR)</option>
                  <option value="bloque_8">Bloque de Cemento 8" (Muro de Carga)</option>
                  <option value="drywall">Drywall / Sheetrock (División Ligera)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => setAddRoomModalOpen(false)}
                className="px-3 py-1.5 rounded-xl border border-border text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAddRoomConfirm}
                className="px-4 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold"
              >
                Incorporar al Plano
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
